import { ButtonERP } from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  InfiniteData,
  QueryObserverResult,
  RefetchOptions,
} from "@tanstack/react-query"
import { useCuentaContableEmpresaCuentaBancaria } from "api/CuentaContableEmpresa.hook"
import { EntidadBancariaDtoProps } from "api/DTOs/EntidadBancariaDto"
import { GetCuentasBancariasByFiltersDtoProps } from "api/DTOs/GetCuentasBancariasByFiltersDto"
import { EntidadBancariaDtoIPagedEnumerableProps } from "api/Schemas/EntidadBancariaDtoIPagedEnumerable"
import DropdownBanks from "common/components/DropdownBanks/DropdownBanks"
import { DropdownCuentasContablesConModal } from "common/components/DropdownsCuentasContables/DropdownCuentasContablesConModal/DropdownCuentasContablesConModal"
import useBankAccountForm from "features/Tesoreria/hooks/useBankAccountForm"

import { ErrorMessage, FormikErrors, FormikTouched } from "formik"
import { InputText } from "primereact/inputtext"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { BankAccountFormValues } from "./constants/constants"

interface NewCuentasBancariasFormProps {
  setFieldValue: (field: string, value: string | null) => void
  values: BankAccountFormValues
  handleOpenCreateModal: () => void
  handleSubmit: () => void
  data?: GetCuentasBancariasByFiltersDtoProps
  errors: FormikErrors<BankAccountFormValues>
  touched: FormikTouched<BankAccountFormValues>
  isEditView: boolean
  setKeyWord: React.Dispatch<React.SetStateAction<string>>
  dataBank:
    | InfiniteData<EntidadBancariaDtoIPagedEnumerableProps, unknown>
    | undefined
  fetchNextPage: (
    options?: RefetchOptions | undefined
  ) => Promise<
    QueryObserverResult<
      InfiniteData<EntidadBancariaDtoIPagedEnumerableProps, unknown>,
      Error
    >
  >
  isFetchingNextPage: boolean
  refetch: (
    options?: RefetchOptions | undefined
  ) => Promise<
    QueryObserverResult<
      InfiniteData<EntidadBancariaDtoIPagedEnumerableProps, unknown>,
      Error
    >
  >
}

const NewCuentasBancariasForm = ({
  setFieldValue,
  values,
  handleOpenCreateModal,
  handleSubmit,
  data,
  errors,
  touched,
  isEditView,
  setKeyWord,
  dataBank,
  fetchNextPage,
  isFetchingNextPage,
  refetch,
}: NewCuentasBancariasFormProps) => {
  const { t } = useTranslation()
  const [showCollapsable, setShowCollapsable] = useState(false)
  const [bankList, setBankList] = useState<EntidadBancariaDtoProps[]>([])

  const { selectedBank, isBicEditable, handleSelectBank, handleGetIban } =
    useBankAccountForm({
      bankList,
      setFieldValue,
      setKeyWord,
    })

  const { cuentasContablesEmpresas: cuentasContablesCuentasBancarias } =
    useCuentaContableEmpresaCuentaBancaria()

  const handleSelectCtaContable = ({ value }: { value: string }) => {
    setFieldValue("cuentaContableEmpresaId", value)
  }

  const handleIbanInput = async (value: string) => {
    handleGetIban(value, setFieldValue)
    setFieldValue("iban", value)
  }

  const handleAcreedor = (value: string) => {
    setFieldValue("identificadorAcreedor", value)
  }

  const handlePresentador = (value: string) => {
    setFieldValue("identificadorPresentador", value)
  }

  const handleNombre = (value: string | null) => {
    setFieldValue("nombre", value)
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit()
      }}
    >
      <div className='tw-mb-6 tw-w-full'>
        <div className='tw-mb-4'>
          <label htmlFor='iban'>{t("bancos.table.modal.labelIban")}</label>
          <div className='tw-flex tw-flex-col tw-w-full'>
            <InputText
              onChange={({
                target: { value },
              }: React.ChangeEvent<HTMLInputElement>) => {
                handleIbanInput(value)
              }}
              id='iban'
              name='iban'
              type='text'
              className={`${
                errors.iban && touched.iban
                  ? "!tw-border-danger"
                  : "!tw-border-neutral-25"
              }`}
              placeholder={`${t("bancos.table.modal.placeholderIban")}`}
              value={values?.iban ?? ""}
              disabled={Boolean(data)}
            />
            {touched?.iban && errors?.iban && (
              <ErrorMessage
                name='iban'
                className='tw-text-danger tw-text-left tw-text-xs'
                component='div'
              />
            )}
          </div>
        </div>
        <div className='tw-mb-4 tw-flex tw-gap-4 tw-w-full'>
          <div className='tw-w-1/2'>
            <label htmlFor='bankId'>{t("bancos.table.modal.labelBank")}</label>
            <DropdownBanks
              id='bankId'
              name='bankId'
              value={isEditView ? values?.entidadBancariaId : selectedBank?.id}
              onchange={(e: { value: string }) => {
                handleSelectBank(e.value)
              }}
              bankList={bankList}
              setBankList={setBankList}
              setKeyWord={setKeyWord}
              dataBank={dataBank}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
              refetch={refetch}
              panelClassName='!tw-z-[2302]'
            />
          </div>
          <div className='tw-w-1/2'>
            <label htmlFor='bic'>{t("bancos.table.modal.labelSwiftBic")}</label>
            <div className='tw-flex tw-flex-col tw-w-full'>
              <InputText
                id='bic'
                name='bic'
                type='text'
                value={values?.bic ?? ""}
                placeholder={data?.bic ?? ""}
                disabled={!isBicEditable || Boolean(data)}
                className={`${
                  errors.bic && touched.bic
                    ? "!tw-border-danger"
                    : "!tw-border-neutral-25"
                }`}
              />
              {touched?.bic && errors?.bic && (
                <ErrorMessage
                  name='bic'
                  className='tw-text-danger tw-text-left'
                  component='div'
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div>
        <div
          className='tw-flex tw-flex-row tw-w-full tw-text-info tw-mb-4'
          onClick={() => setShowCollapsable(!showCollapsable)}
        >
          <span className='tw-cursor-pointer'>
            {t("remesas.edit.form.advanceOption")}
          </span>
          <span className='tw-w-6 tw-h-6 tw-flex tw-justify-center tw-items-center tw-mr-1'>
            {showCollapsable ? (
              <FontAwesomeIcon icon={["fal", "angle-down"]} />
            ) : (
              <FontAwesomeIcon icon={["fal", "angle-right"]} />
            )}
          </span>
        </div>
      </div>

      <div
        className={`${
          showCollapsable
            ? "tw-h-full tw-opacity-100"
            : "tw-h-0 tw-opacity-0 tw-hidden"
        } tw-transition-all`}
      >
        <div className='tw-w-full tw-flex tw-flex-col tw-mt-4'>
          <span className='tw-mb-2'>
            {t("remesas.edit.form.nameAccountLabel")}
          </span>
          <InputText
            id='nombre'
            name='nombre'
            type='text'
            value={values?.nombre ?? ""}
            onChange={({
              target: { value },
            }: React.ChangeEvent<HTMLInputElement>) => {
              handleNombre(value !== "" ? value : null)
            }}
          />
          {touched?.nombre && errors?.nombre && (
            <ErrorMessage
              name='nombre'
              className='tw-text-danger tw-text-left'
              component='div'
            />
          )}
        </div>
        <div className='tw-w-full tw-flex tw-flex-row tw-mt-4'>
          <div className='tw-w-1/2 tw-flex tw-flex-col tw-mr-1'>
            <span className='tw-mb-2'>
              {t("remesas.edit.form.idCreditorLabel")}
            </span>
            <InputText
              id='identificadorAcreedor'
              name='identificadorAcreedor'
              type='text'
              value={values?.identificadorAcreedor}
              onChange={({
                target: { value },
              }: React.ChangeEvent<HTMLInputElement>) => {
                handleAcreedor(value)
              }}
            />
            {touched?.identificadorAcreedor &&
              errors?.identificadorAcreedor && (
                <ErrorMessage
                  name='identificadorAcreedor'
                  className='tw-text-danger tw-text-left'
                  component='div'
                />
              )}
          </div>
          <div className='tw-w-1/2 tw-flex tw-flex-col tw-ml-1'>
            <span className='tw-mb-2'>
              {t("remesas.edit.form.idRepresentativeLabel")}
            </span>
            <InputText
              id='identificadorPresentador'
              name='identificadorPresentador'
              type='text'
              value={values?.identificadorPresentador}
              onChange={({ target: { value } }: $TSMigrationFixMe) => {
                handlePresentador(value)
              }}
            />
            {touched?.identificadorPresentador &&
              errors?.identificadorPresentador && (
                <ErrorMessage
                  name='identificadorPresentador'
                  className='tw-text-danger tw-text-left'
                  component='div'
                />
              )}
          </div>
        </div>
        <div className='tw-mt-4'>
          <label
            htmlFor='iban'
            className='tw-mb-2'
          >
            {t("bancos.table.modal.labelCuentaCont")}
          </label>

          <DropdownCuentasContablesConModal
            cuentasContablesOptions={cuentasContablesCuentasBancarias}
            dropdownValue={values?.cuentaContableEmpresaId}
            onChange={handleSelectCtaContable}
          />
        </div>
      </div>
      <div className='tw-flex tw-justify-end tw-gap-4 tw-mt-8'>
        <ButtonERP
          customize={{
            type: "white",
            size: "md",
          }}
          type='button'
          onClick={() => {
            handleOpenCreateModal()
            setKeyWord("")
          }}
          label={`${t("bancos.table.modal.cancelButton")}`}
        />
        <ButtonERP
          customize={{
            type: isEditView ? "primary" : "secondary",
            size: "md",
          }}
          type='submit'
          label={
            isEditView
              ? `${t("bancos.table.modal.headerSave")}`
              : `${t("bancos.table.modal.addButton")}`
          }
        />
      </div>
    </form>
  )
}

export default NewCuentasBancariasForm
